import { useCallback, useId, useState, type ReactNode } from "react";
import { Lock } from "lucide-react";
import { useCustomerDataAdapter } from "@shared-core/data/CustomerDataAdapterContext";
import type {
  CustomerCatalogueOption,
  CustomerStockListPaper,
} from "@shared-core/data/customer-api-types";
import { useStockListSearch } from "@shared-core/domain/useStockListSearch";
import { customerLabelOf } from "@shared-core/domain/customer-option-wording";
import { OptionTileGroup } from "../OptionTileGroup";
import { OptionSelect } from "./OptionSelect";
import {
  isSingleChoice,
  isTileGroup,
  paperGroupsOf,
  typedPaperValue,
  type TypedPaper,
} from "./option-groups";

// One option group in the buy box, in whichever form its choices call for: a
// muted line when there is nothing to choose, tiles for a few choices, a
// dropdown for many. A paper group is two groups, type then weight (R204); a group that takes a paper we don't list
// ends its types with that choice, and a text box replaces the weights (spec 32 §5.7). A tile group takes the full
// width of the options grid; the others sit two to a row where the buy box is wide enough.
export function OptionGroup({
  option,
  value,
  onChange,
  unavailableCodes,
  requirementHints,
  mostPopularCode,
  itemId,
}: {
  option: CustomerCatalogueOption;
  value: string;
  onChange: (code: string) => void;
  unavailableCodes?: ReadonlySet<string>;
  requirementHints?: ReadonlyMap<string, string>;
  mostPopularCode?: string;
  /** The item the group belongs to, for its typed paper's stock list search. */
  itemId?: string;
}) {
  const paper = paperGroupsOf(option, value, unavailableCodes, mostPopularCode);
  if (paper) {
    return (
      <>
        <OptionGroup
          option={paper.types}
          value={paper.typeValue}
          onChange={(type) => onChange(paper.codeForType(type))}
          unavailableCodes={paper.unavailableTypes}
          mostPopularCode={paper.mostPopularType}
        />
        {paper.typed?.isChosen && (
          <TypedPaperInput
            typed={paper.typed}
            itemId={itemId}
            fieldKey={option.key}
            onChange={(text) => onChange(typedPaperValue(text))}
          />
        )}
        {paper.weights && (
          <OptionGroup
            option={paper.weights}
            value={value}
            onChange={onChange}
            unavailableCodes={unavailableCodes}
            requirementHints={requirementHints}
            mostPopularCode={mostPopularCode}
          />
        )}
      </>
    );
  }
  if (isSingleChoice(option)) return <FixedOptionLine option={option} />;
  if (isTileGroup(option)) {
    return (
      <OptionTileGroup
        option={option}
        value={value}
        onChange={onChange}
        unavailableCodes={unavailableCodes}
        mostPopularCode={mostPopularCode}
        className="col-span-full"
      />
    );
  }
  return (
    <OptionSelect
      option={option}
      value={value}
      onChange={onChange}
      unavailableCodes={unavailableCodes}
      requirementHints={requirementHints}
      mostPopularCode={mostPopularCode}
    />
  );
}

// The paper the customer names when we don't list it: our team prices it, so the line is "Quote on request". Where the
// group has our stock list, what they type is searched on it and a paper they choose fills the box with its name; any
// other text stays as typed (manual RFQ slice 4).
function TypedPaperInput({
  typed,
  itemId,
  fieldKey,
  onChange,
}: {
  typed: TypedPaper;
  itemId?: string;
  fieldKey: string;
  onChange: (text: string) => void;
}) {
  const adapter = useCustomerDataAdapter();
  const [isOpen, setIsOpen] = useState(false);
  const listboxId = useId();
  const hintId = useId();
  const search = useCallback(
    (query: string) =>
      adapter.searchCatalogueStockList(itemId ?? "", fieldKey, query),
    [adapter, itemId, fieldKey],
  );
  const { papers } = useStockListSearch<CustomerStockListPaper>(
    typed.text,
    search,
    typed.hasStockList && Boolean(itemId) && isOpen,
  );
  const isListOpen = isOpen && papers.length > 0;
  return (
    <div className="relative col-span-full flex flex-col gap-1.5">
      <input
        type="text"
        role={typed.hasStockList ? "combobox" : undefined}
        aria-expanded={typed.hasStockList ? isListOpen : undefined}
        aria-controls={typed.hasStockList ? listboxId : undefined}
        aria-autocomplete={typed.hasStockList ? "list" : undefined}
        aria-label={typed.label}
        aria-describedby={typed.hint ? hintId : undefined}
        value={typed.text}
        maxLength={typed.maxLength ?? undefined}
        onChange={(event) => {
          onChange(event.target.value);
          setIsOpen(true);
        }}
        onFocus={() => setIsOpen(true)}
        onBlur={() => setIsOpen(false)}
        className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
      />
      {isListOpen && (
        <ul
          id={listboxId}
          role="listbox"
          aria-label="Papers we can get"
          // Keeps focus in the box while a paper is clicked.
          onMouseDown={(event) => event.preventDefault()}
          className="absolute top-11 z-20 max-h-64 w-full overflow-auto rounded-md border border-border bg-card shadow-md"
        >
          {papers.map((paper) => (
            <li
              key={paper.id}
              role="option"
              aria-selected={false}
              onClick={() => {
                onChange(paper.name);
                setIsOpen(false);
              }}
              className="cursor-pointer px-3 py-2 text-sm hover:bg-muted"
            >
              {paper.name}
            </li>
          ))}
        </ul>
      )}
      {typed.hint && (
        <p id={hintId} className="text-xs text-muted-foreground">
          {typed.hasStockList
            ? `Start typing to search the papers we can get, or ${typed.hint.charAt(0).toLowerCase()}${typed.hint.slice(1)}`
            : typed.hint}
        </p>
      )}
    </div>
  );
}

// No real choice to make: a fixed field, or a group with one option. Same
// locked language as the staff item page (a lock and a muted value).
function FixedOptionLine({ option }: { option: CustomerCatalogueOption }) {
  const only = option.choices[0];
  if (!only) return null;
  const groupLabel = customerLabelOf(option);
  const onlyLabel = customerLabelOf(only);
  const isSameWord = onlyLabel.toLowerCase() === groupLabel.toLowerCase();
  return (
    <p className="flex min-w-0 items-center gap-1.5 text-sm text-muted-foreground">
      <Lock className="size-3.5 shrink-0" aria-hidden="true" />
      <span className="truncate">
        {isSameWord ? groupLabel : `${groupLabel}: ${onlyLabel}`}
      </span>
    </p>
  );
}

/** The options grid: dropdowns and locked lines two to a row once the buy box is wide enough (container query), tiles full width. */
export function OptionGrid({ children }: { children: ReactNode }) {
  return (
    <div className="@container">
      <div className="grid grid-cols-1 gap-x-3 gap-y-4 @sm:grid-cols-2">
        {children}
      </div>
    </div>
  );
}
